import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["column"]

  connect() {
    this.draggedCard = null
    this.placeholder = null

    this.columnTargets.forEach(column => {
      column.addEventListener("dragover", this.onDragOver.bind(this))
      column.addEventListener("dragenter", this.onDragEnter.bind(this))
      column.addEventListener("dragleave", this.onDragLeave.bind(this))
      column.addEventListener("drop", this.onDrop.bind(this))
    })

    // Make all deal cards draggable
    this.element.querySelectorAll("[data-deal-id]").forEach(card => {
      card.setAttribute("draggable", "true")
      card.addEventListener("dragstart", this.onDragStart.bind(this))
      card.addEventListener("dragend", this.onDragEnd.bind(this))
    })
  }

  onDragStart(event) {
    this.draggedCard = event.currentTarget
    this.draggedCard.classList.add("opacity-50", "rotate-2")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedCard.dataset.dealId)

    // Create a placeholder
    this.placeholder = document.createElement("div")
    this.placeholder.classList.add(
      "border-2", "border-dashed", "border-mod-warm-400",
      "bg-mod-warm-50", "rounded-lg", "h-20", "transition-all"
    )
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const column = event.currentTarget
    const afterElement = this.getDragAfterElement(column, event.clientY)

    if (!this.placeholder.parentNode || this.placeholder.parentNode !== column) {
      if (afterElement) {
        column.insertBefore(this.placeholder, afterElement)
      } else {
        column.appendChild(this.placeholder)
      }
    } else if (afterElement) {
      column.insertBefore(this.placeholder, afterElement)
    } else {
      column.appendChild(this.placeholder)
    }
  }

  onDragEnter(event) {
    event.preventDefault()
    event.currentTarget.closest(".bg-mod-warm-100")?.classList.add("ring-2", "ring-mod-warm-400")
  }

  onDragLeave(event) {
    // Only remove highlight if leaving the column entirely
    const column = event.currentTarget
    const rect = column.getBoundingClientRect()
    if (
      event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom
    ) {
      column.closest(".bg-mod-warm-100")?.classList.remove("ring-2", "ring-mod-warm-400")
    }
  }

  onDrop(event) {
    event.preventDefault()
    const column = event.currentTarget
    column.closest(".bg-mod-warm-100")?.classList.remove("ring-2", "ring-mod-warm-400")

    if (!this.draggedCard) return

    const dealId = event.dataTransfer.getData("text/plain")
    const newStage = column.dataset.stage

    // Replace placeholder with the card
    if (this.placeholder.parentNode) {
      column.insertBefore(this.draggedCard, this.placeholder)
      this.placeholder.remove()
    } else {
      column.appendChild(this.draggedCard)
    }

    this.draggedCard.classList.remove("opacity-50", "rotate-2")

    // Update column counts
    this.updateColumnCounts()

    // Persist the stage change
    this.updateDealStage(dealId, newStage)
  }

  onDragEnd(event) {
    if (this.draggedCard) {
      this.draggedCard.classList.remove("opacity-50", "rotate-2")
    }
    if (this.placeholder?.parentNode) {
      this.placeholder.remove()
    }
    // Remove all column highlights
    this.columnTargets.forEach(col => {
      col.closest(".bg-mod-warm-100")?.classList.remove("ring-2", "ring-mod-warm-400")
    })
    this.draggedCard = null
    this.placeholder = null
  }

  getDragAfterElement(column, y) {
    const draggableElements = [
      ...column.querySelectorAll("[data-deal-id]:not(.opacity-50)")
    ]

    let closest = null
    let closestOffset = Number.NEGATIVE_INFINITY

    draggableElements.forEach(child => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      if (offset < 0 && offset > closestOffset) {
        closestOffset = offset
        closest = child
      }
    })

    return closest
  }

  updateColumnCounts() {
    this.columnTargets.forEach(column => {
      const count = column.querySelectorAll("[data-deal-id]").length
      const badge = column.closest(".flex-shrink-0")?.querySelector(".rounded-full")
      if (badge) {
        badge.textContent = count
      }
    })
  }

  async updateDealStage(dealId, stage) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const card = this.element.querySelector(`[data-deal-id="${dealId}"]`)

    try {
      const response = await fetch(`/deals/${dealId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ deal: { stage: stage } })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      // Brief success flash on the card
      card?.classList.add("ring-2", "ring-green-400")
      setTimeout(() => card?.classList.remove("ring-2", "ring-green-400"), 1000)
    } catch (error) {
      console.error("Failed to update deal stage:", error)
      // Show error indicator
      card?.classList.add("ring-2", "ring-red-400")
      setTimeout(() => card?.classList.remove("ring-2", "ring-red-400"), 2000)
    }
  }
}
